import {
  EmailAuthProvider,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  type DocumentReference,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  type QueryConstraint,
} from 'firebase/firestore';

import { firebaseAuth, firebaseDb } from '@client/services/firebase.config';
import { isOffline } from '@client/utils/document.util';
import { logger } from '@client/utils/logger.util';
import { transformMetadata } from '@client/utils/metadata.util';
import {
  ESort,
  EErrorType,
  type IAddProps,
  type IDeleteProps,
  type IDocRefProps,
  type IFindByIdProps,
  type IFirestoreEntity,
  type IReadAllProps,
  type IReadByProps,
  type IReadByRefProps,
  type IUpdateProps,
  type IWriteProps,
} from '@common/types';

export const firebaseGetDocRef = ({
  collectionPath,
  document,
}: IDocRefProps) => {
  if (document) {
    return doc(firebaseDb, collectionPath, document);
  }

  const msg = `Document must be defined. collectionPath: ${collectionPath}, document: ${document}`;
  logger.error('firebaseGetDocRef', msg);
  throw new Error(msg);
};

/**
 * Check if Firestore is available (not offline)
 * @throws {Error} If Firestore is offline
 * @returns {Promise<void>}
 * @async
 */
async function checkFirestore(): Promise<void> {
  if (isOffline()) throw new Error(EErrorType.OFFLINE);
}

/**
 * Validate if the current user is the owner of the document
 * @param {DocumentReference} docRef - Document reference to validate
 * @throws {Error} If the user is not authenticated or not the owner
 * @returns {Promise<void>}
 * @async
 */
async function validateOwnership(docRef: DocumentReference): Promise<void> {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) throw new Error(EErrorType.NOT_AUTHENTICATED);

  const docSnap = await getDoc(docRef);
  const data = docSnap.data();

  if (!data) {
    throw new Error(EErrorType.NOT_FOUND);
  }

  const ownerUid = data?.metadata?.createdBy?.uid;
  if (!ownerUid || ownerUid !== currentUser.uid) {
    throw new Error(EErrorType.NOT_OWNER);
  }
}

export const firebaseAddDoc = async <T extends IFirestoreEntity>({
  collectionPath,
  data = {},
}: IAddProps) => {
  try {
    await checkFirestore();
    const reference = await addDoc(
      collection(firebaseDb, collectionPath),
      data,
    );
    return firebaseGetById<T>({ collectionPath, reference });
  } catch (error) {
    logger.error(`firebaseAddDoc: ${collectionPath}`, error);
    throw error;
  }
};

export const firebaseSetDoc = async ({
  collectionPath,
  document,
  data = {},
}: IWriteProps) => {
  await checkFirestore();
  const docRef = firebaseGetDocRef({ collectionPath, document });

  return setDoc(docRef, data)
    .then(async () => firebaseGetById({ collectionPath, reference: docRef }))
    .catch(async (error: Error) => {
      logger.error(`firebaseSetDoc: ${collectionPath}`, error);
      throw error;
    });
};

export const firebaseGetBy = async <T = any>({
  collectionPath,
  optional = {},
  wheres,
}: IReadByProps): Promise<T[]> => {
  try {
    if (!wheres?.length) throw new Error(EErrorType.INVALID_QUERY);

    const constraints: QueryConstraint[] = [];

    // Add additional where filters (e.g. type === 'BUSINESS')
    for (const cond of wheres) {
      constraints.push(where(cond.field, cond.operator ?? '==', cond.value));
    }

    if (optional.order) {
      constraints.push(
        orderBy(optional.order, optional.orderDirection || ESort.DESC),
      );
    }

    if (optional.limit !== undefined) {
      constraints.push(limit(optional.limit));
    }

    const q = query(collection(firebaseDb, collectionPath), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  } catch (error) {
    logger.error(`firebaseGetBy: ${collectionPath}`, error);
    throw error;
  }
};

export const firebaseGetAll = async <T extends IFirestoreEntity>({
  collectionPath,
  restrictToOwner = true,
}: IReadAllProps & {
  restrictToOwner?: boolean;
}): Promise<T[]> => {
  try {
    await checkFirestore();
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser && restrictToOwner) {
      throw new Error(EErrorType.NOT_AUTHENTICATED);
    }

    const snapshot = await getDocs(collection(firebaseDb, collectionPath));
    const allData = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];

    return restrictToOwner
      ? allData.filter(
          (item) => item.metadata?.createdBy?.uid === currentUser?.uid,
        )
      : allData;
  } catch (error) {
    logger.error(`firebaseGetAll: ${collectionPath}`, error);
    throw error;
  }
};

export const firebaseGetByRef = async ({ reference }: IReadByRefProps) => {
  await checkFirestore();
  await validateOwnership(reference);
  const snapshot = await getDoc(reference);
  return snapshot.data();
};

export const firebaseGetById = async <T extends IFirestoreEntity>(
  props: IFindByIdProps,
): Promise<T & { id: string }> => {
  const { reference, collectionPath, document } = props;
  await checkFirestore();

  const docRef =
    reference ??
    firebaseGetDocRef({ collectionPath, document } as IDocRefProps);

  try {
    await validateOwnership(docRef);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
      throw new Error(`${EErrorType.NOT_FOUND}: ${docRef.path}`);
    }

    const data = snapshot.data() as Omit<T, 'id'>;

    return {
      ...data,
      id: snapshot.id,
      metadata: transformMetadata<T>(data.metadata),
    } as T & { id: string };
  } catch (error) {
    logger.error(`firebaseGetById: ${collectionPath}`, error);
    throw error;
  }
};

export const firebaseUpdateDoc = async <T extends IFirestoreEntity>({
  collectionPath,
  document,
  data,
}: IUpdateProps) => {
  await checkFirestore();
  const docRef = firebaseGetDocRef({ collectionPath, document });

  try {
    await validateOwnership(docRef);
    await updateDoc(docRef, data);
    return firebaseGetById<T>({ collectionPath, reference: docRef });
  } catch (error) {
    logger.error(`firebaseUpdateDoc: ${collectionPath}`, error);
    throw error;
  }
};

export const firebaseDeleteDoc = async ({
  collectionPath,
  document,
}: IDeleteProps) => {
  await checkFirestore();
  const docRef = firebaseGetDocRef({ collectionPath, document });

  try {
    await validateOwnership(docRef);
    await deleteDoc(docRef);
    return { success: true, path: docRef.path };
  } catch (error) {
    logger.error(`firebaseDeleteDoc: ${collectionPath}`, error);
    throw error;
  }
};

export const checkCredentialWithLink = ({ email }: { email: string }) =>
  EmailAuthProvider.credentialWithLink(email, window.location.href);

export const checkSignInWithEmailLink = async ({
  email,
}: {
  email: string;
}) => {
  // Confirm the link is a sign-in with an email link.
  if (isSignInWithEmailLink(firebaseAuth, window.location.href)) {
    // Additional state parameters can also be passed via URL.
    // This can be used to continue the user's intended action before triggering
    // the sign-in operation.
    // Get the email if available. This should be available if the user completes
    // the flow on the same device where they started it.

    if (!email) {
      // User opened the link on a different device. To prevent session fixation
      // attacks, ask the user to provide the associated email again.
      logger.error('checkSignInWithEmailLink: Invalid email', {
        email,
        message: 'Please provide your email for confirmation',
      });

      return false;
    }

    // The client SDK will parse the code from the link for you.
    return signInWithEmailLink(firebaseAuth, email, window.location.href)
      .then(
        (result) =>
          // You can access the new user by importing getAdditionalUserInfo
          // and calling it with a result:
          result,
      )
      .catch(async (error: Error) => {
        // Some error occurred, you can inspect the code: error.code
        // Common errors could be invalid email and invalid or expired OTPs.
        logger.error('checkSignInWithEmailLink', error);

        throw error;
      });
  }
};
